/**
 * Format currency amount to Ghana Cedis (GHS)
 * @param {number|string} amount - The amount to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    currency = 'GHS',
    locale = 'en-GH',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = true
  } = options;

  if (amount === null || amount === undefined || amount === '') {
    return showSymbol ? '₵ 0.00' : '0.00';
  }

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) {
    return showSymbol ? '₵ 0.00' : '0.00';
  }

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: showSymbol ? 'currency' : 'decimal',
      currency: currency,
      minimumFractionDigits,
      maximumFractionDigits,
    });

    let formatted = formatter.format(numericAmount);
    
    // For Ghana Cedis, replace the default currency symbol with ₵
    if (showSymbol && currency === 'GHS') {
      formatted = formatted.replace(/GHS|₵\s*/, '₵ ');
    }
    
    return formatted;
  } catch {
    return showSymbol ? `₵ ${numericAmount.toFixed(2)}` : numericAmount.toFixed(2);
  }
};

/**
 * Format currency for display in tables or compact spaces
 * @param {number|string} amount - The amount to format
 * @returns {string} Compact formatted currency string
 */
export const formatCurrencyCompact = (amount) => {
  return formatCurrency(amount, { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  });
};

/**
 * Format currency without symbol (for input fields)
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted number string without currency symbol
 */
export const formatCurrencyInput = (amount) => {
  return formatCurrency(amount, { showSymbol: false });
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string to parse
 * @returns {number} Parsed number
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString) return 0;
  
  // Remove currency symbols, spaces, and commas
  const cleanString = currencyString
    .toString()
    .replace(/[₵R$€£¥₹,\s]/g, '')
    .trim();
  
  const parsed = parseFloat(cleanString);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Validate if a string is a valid currency amount
 * @param {string} value - Value to validate
 * @returns {boolean} True if valid currency amount
 */
export const isValidCurrencyAmount = (value) => {
  if (!value) return false;
  const parsed = parseCurrency(value);
  return !isNaN(parsed) && parsed >= 0;
};

/**
 * Convert currency amount to different denomination
 * @param {number} amount - Amount to convert
 * @param {string} from - Source denomination (e.g., 'GHS', 'USD')
 * @param {string} to - Target denomination
 * @param {number} exchangeRate - Exchange rate (optional, defaults to 1)
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, from = 'GHS', to = 'GHS', exchangeRate = 1) => {
  if (from === to) return amount;
  return amount * exchangeRate;
};

// Default export for convenience
export default {
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyInput,
  parseCurrency,
  isValidCurrencyAmount,
  convertCurrency
};
